import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Dialog, DialogContent } from "./ui/dialog";
import { X, Youtube, Bell, Loader2, Play } from "lucide-react";
import { useContent } from "../content/ContentContext";
import { PremiereCountdown } from "./PremiereCountdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let ytApiPromise = null;
const loadYouTubeApi = () => {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (!ytApiPromise) {
    ytApiPromise = new Promise((resolve) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        prev?.();
        resolve(window.YT);
      };
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.head.appendChild(tag);
    });
  }
  return ytApiPromise;
};

const trackTeaserPlay = () => {
  if (localStorage.getItem("wntt_teaser_played")) return;
  localStorage.setItem("wntt_teaser_played", "1");
  axios.post(`${API}/track/teaser-play`).catch(() => {});
};

const NotifyForm = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/subscribe`, { email: email.trim() });
      toast.success(
        data.status === "already_subscribed"
          ? "You're already part of the conversation."
          : "You're on the list. We'll tell you the moment this conversation premieres."
      );
      setEmail("");
    } catch {
      toast.error("Something interrupted the conversation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="watch-modal-notify-form">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter your email"
        className="flex-1 bg-transparent border border-white/15 focus:border-gold/70 rounded-full px-6 py-3.5 text-sm text-ivory placeholder:text-smoke/70 outline-none transition-colors duration-300"
        data-testid="watch-modal-email-input"
      />
      <button
        type="submit"
        disabled={loading}
        className="inline-flex items-center justify-center gap-3 bg-gold hover:bg-gold-light disabled:opacity-60 text-ink text-[11px] font-semibold tracking-[0.24em] uppercase px-7 py-3.5 rounded-full transition-colors duration-300"
        data-testid="watch-modal-notify-button"
      >
        {loading ? <Loader2 size={13} className="animate-spin" /> : <Bell size={13} />}
        Notify me
      </button>
    </form>
  );
};

export const WatchModal = ({ episode, open, onOpenChange, onSelectEpisode }) => {
  const iframeRef = useRef(null);
  const timerRef = useRef(null);
  const [upNextIn, setUpNextIn] = useState(null);
  const { SOCIALS, EPISODES } = useContent();

  const hasVideo = Boolean(episode?.videoId);
  const nextEpisode = hasVideo
    ? EPISODES.filter((e) => e.videoId && e.num > episode.num).sort((a, b) => a.num - b.num)[0] || null
    : null;

  const startCountdown = () => {
    if (!nextEpisode) return;
    setUpNextIn(6);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setUpNextIn((s) => {
        if (s === null || s <= 1) {
          clearInterval(timerRef.current);
          onSelectEpisode?.(nextEpisode);
          return null;
        }
        return s - 1;
      });
    }, 1000);
  };

  const cancelUpNext = () => {
    clearInterval(timerRef.current);
    setUpNextIn(null);
  };

  const playNow = () => {
    clearInterval(timerRef.current);
    setUpNextIn(null);
    onSelectEpisode?.(nextEpisode);
  };

  // Attach the YouTube IFrame API to detect the episode ending
  useEffect(() => {
    if (!open || !hasVideo || !nextEpisode) return;
    let player;
    let cancelled = false;
    loadYouTubeApi().then((YT) => {
      if (cancelled || !iframeRef.current) return;
      player = new YT.Player(iframeRef.current, {
        events: {
          onStateChange: (e) => {
            if (e.data === YT.PlayerState.ENDED) startCountdown();
          },
        },
      });
    });
    window.__wntt_upnext = startCountdown;
    return () => {
      cancelled = true;
      try {
        player?.destroy();
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, episode?.videoId]);

  // Reset the overlay whenever the episode changes or the modal closes
  useEffect(() => {
    cancelUpNext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episode?.videoId, open]);

  if (!episode) return null;
  const hasTeaser = !hasVideo && Boolean(episode.teaser);
  const hasMedia = hasVideo || hasTeaser;
  const nextUp = EPISODES.filter((e) => e.videoId && e.num !== episode.num);

  const badge = hasVideo ? "Now playing" : hasTeaser ? "Official teaser" : "Premiering soon";
  const descSuffix = hasVideo
    ? " Also available on our channel — subscribe so you never miss the next conversation."
    : hasTeaser
      ? " The full conversation premieres Monday, 17 August 2026 at 12 PM. Leave your email and be the first to know the moment it drops."
      : " This conversation hasn't premiered yet. Leave your email and be the first to know the moment it does.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl bg-coal border-white/10 text-ivory p-0 gap-0 [&>button]:hidden max-h-[92vh] overflow-y-auto"
        data-testid="watch-modal"
      >
        <div className="relative aspect-video bg-ink">
          {hasVideo && (
            <iframe
              key={episode.videoId}
              ref={iframeRef}
              src={`https://www.youtube-nocookie.com/embed/${episode.videoId}?autoplay=1&rel=0&enablejsapi=1`}
              title={episode.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              data-testid="watch-modal-player"
            />
          )}
          {hasTeaser && (
            <video
              poster={episode.img}
              autoPlay
              controls
              playsInline
              preload="auto"
              onCanPlay={(e) => e.currentTarget.play().catch(() => {})}
              onPlay={trackTeaserPlay}
              className="absolute inset-0 w-full h-full object-contain bg-black"
              data-testid="watch-modal-player"
            >
              <source src={episode.teaser} type="video/mp4" />
              <source src={episode.teaser.replace(".mp4", ".webm")} type="video/webm" />
            </video>
          )}
          {!hasMedia && (
            <>
              <motion.img
                src={episode.img}
                alt={episode.title}
                className="absolute inset-0 w-full h-full object-cover img-cinematic"
                animate={{ scale: [1, 1.08] }}
                transition={{ duration: 18, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
                data-testid="watch-modal-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-coal via-coal/20 to-transparent" />
            </>
          )}
          <span
            className="absolute top-4 left-4 text-[10px] tracking-[0.3em] uppercase text-gold-light border border-gold/40 bg-ink/60 backdrop-blur-sm px-3 py-1.5 rounded-full pointer-events-none"
            data-testid="watch-modal-premiere-badge"
          >
            {badge}
          </span>
          {!hasMedia && (
            <p className="absolute bottom-4 left-6 overline-label" data-testid="watch-modal-ep-num">
              EPISODE {String(episode.num).padStart(2, "0")}
            </p>
          )}
          {upNextIn !== null && nextEpisode && (
            <div
              className="absolute inset-0 z-10 bg-ink/90 backdrop-blur-md flex flex-col items-center justify-center text-center px-6"
              data-testid="upnext-overlay"
            >
              <p className="text-[10px] tracking-[0.3em] uppercase text-smoke">Up next</p>
              <p className="mt-3 text-gold-light text-[11px] tracking-[0.24em] uppercase">
                EPISODE {String(nextEpisode.num).padStart(2, "0")}
              </p>
              <h4 className="font-display text-xl md:text-2xl mt-1.5 leading-snug" data-testid="upnext-title">
                {nextEpisode.title}
              </h4>
              <p className="mt-3.5 text-sm text-smoke">
                Playing in <span className="text-gold-light font-semibold" data-testid="upnext-countdown">{upNextIn}</span>s
              </p>
              <div className="mt-6 flex items-center gap-3">
                <button
                  onClick={playNow}
                  className="inline-flex items-center gap-2.5 bg-gold hover:bg-gold-light text-ink text-[10px] font-semibold tracking-[0.22em] uppercase px-6 py-3 rounded-full transition-colors duration-300"
                  data-testid="upnext-play-button"
                >
                  <Play size={11} className="fill-current" />
                  Play now
                </button>
                <button
                  onClick={cancelUpNext}
                  className="border border-ivory/25 hover:border-gold/60 hover:text-gold-light text-ivory text-[10px] tracking-[0.22em] uppercase px-6 py-3 rounded-full transition-colors duration-300"
                  data-testid="upnext-cancel-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="p-8 md:p-10">
          <h3 className="font-display text-2xl md:text-3xl leading-snug" data-testid="watch-modal-title">
            {episode.title}
          </h3>
          <p className="text-sm md:text-base text-smoke mt-4 leading-relaxed" data-testid="watch-modal-desc">
            {episode.desc}
            {descSuffix}
          </p>
          {episode.premiere && !hasVideo && <PremiereCountdown date={episode.premiere} className="mt-7" />}
          {!hasVideo && <NotifyForm />}
          <a
            href={SOCIALS.youtube}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-3 border border-gold/40 hover:border-gold text-gold-light hover:text-ivory text-[11px] font-semibold tracking-[0.24em] uppercase px-8 py-4 rounded-full transition-colors duration-300"
            data-testid="watch-modal-youtube-button"
          >
            <Youtube size={15} />
            {hasVideo ? "Watch on YouTube" : "Subscribe on YouTube"}
          </a>
          {onSelectEpisode && nextUp.length > 0 && (
            <div className="mt-9 pt-7 border-t border-white/10" data-testid="watch-next-strip">
              <p className="text-[10px] tracking-[0.3em] uppercase text-smoke mb-4">Watch next</p>
              <div className="flex gap-4 overflow-x-auto pb-2">
                {nextUp.map((ep) => (
                  <button
                    key={ep.num}
                    onClick={() => onSelectEpisode(ep)}
                    className="group shrink-0 w-40 md:w-44 text-left"
                    data-testid={`watch-next-${ep.num}`}
                  >
                    <div className="relative aspect-video overflow-hidden rounded-lg border border-white/10 group-hover:border-gold/50 transition-colors duration-300">
                      <img
                        src={ep.img}
                        alt={ep.title}
                        className="absolute inset-0 w-full h-full object-cover img-cinematic group-hover:scale-105 transition-transform duration-500"
                      />
                      <span className="absolute top-2 left-2 text-[9px] tracking-[0.2em] uppercase bg-ink/70 backdrop-blur-sm text-gold-light px-2 py-1 rounded">
                        EP {String(ep.num).padStart(2, "0")}
                      </span>
                      <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-ink/40">
                        <Play size={20} className="text-ivory fill-current" />
                      </span>
                    </div>
                    <p className="mt-2.5 text-[11px] leading-snug text-ivory/80 group-hover:text-ivory transition-colors duration-300 line-clamp-2">
                      {ep.title}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="absolute top-4 right-4">
          <button
            onClick={() => onOpenChange(false)}
            className="text-ivory/70 hover:text-gold-light transition-colors duration-300"
            aria-label="Close"
            data-testid="watch-modal-close"
          >
            <X size={20} />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
